import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, ArrowLeft, AlertTriangle, Bot, TrendingUp, Copy, Building, Cpu, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';

export const TermsOfServicePage: React.FC = () => {
    const { t } = useLanguage();

    return (
        <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="mb-12">
                    <Link to={ROUTES.HOME} className="flex items-center text-gray-400 hover:text-white transition-colors group">
                        <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                        {t('termsOfService.backToHome')}
                    </Link>
                </div>

                <div>
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-orange-500/10 mb-8 ring-1 ring-orange-500/20">
                            <FileText size={40} className="text-orange-500" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black mb-6">{t('termsOfService.title')}</h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            {t('termsOfService.subtitle')}
                        </p>
                    </div>

                    <div className="space-y-8 text-gray-300 leading-relaxed">

                        {/* Last Updated */}
                        <div className="bg-surface-dark border border-gray-800 rounded-2xl p-6 text-sm text-gray-400">
                            <strong>{t('termsOfService.lastUpdated')}</strong> {t('termsOfService.lastUpdatedDate')}
                        </div>

                        {/* 1. Acceptance */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <ShieldCheck size={24} className="text-orange-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('termsOfService.s1.title')}</h2>
                                    <p>
                                        <a href="https://ctc-corp.eu/" className="text-orange-400 hover:text-orange-300 underline underline-offset-2" target="_blank" rel="noopener noreferrer">https://ctc-corp.eu/</a>{' '}
                                        {t('termsOfService.s1.p')}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 2. Finance Division */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <TrendingUp size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-4">{t('termsOfService.s2.title')}</h2>
                                    <p className="mb-4">
                                        {t('termsOfService.s2.p')}
                                    </p>
                                    <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-5">
                                        <div className="flex items-start gap-3 mb-4">
                                            <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                                            <p className="text-amber-300 font-semibold text-sm">
                                                {t('termsOfService.s2.warningTitle')}
                                            </p>
                                        </div>
                                        <ul className="list-disc list-inside space-y-2 text-sm text-amber-200/80 ml-7">
                                            <li>{t('termsOfService.s2.w1')}</li>
                                            <li>{t('termsOfService.s2.w2')}</li>
                                            <li>{t('termsOfService.s2.w3')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 3. CTC Blue Strike */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <Bot size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-6">{t('termsOfService.s3.title')}</h2>
                                    <div className="space-y-5">
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s3.h1')}</h3>
                                            <p className="text-sm">{t('termsOfService.s3.h1p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s3.h2')}</h3>
                                            <p className="text-sm">{t('termsOfService.s3.h2p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s3.h3')}</h3>
                                            <p className="text-sm mb-3">{t('termsOfService.s3.h3p1')}</p>
                                            <p className="text-sm mb-3">{t('termsOfService.s3.h3p2')}</p>
                                            <p className="text-sm">{t('termsOfService.s3.h3p3')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s3.h4')}</h3>
                                            <p className="text-sm">{t('termsOfService.s3.h4p')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 4. CTC Algo Engine 2.0 */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <TrendingUp size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-6">{t('termsOfService.s4.title')}</h2>
                                    <div className="space-y-5">
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s4.h1')}</h3>
                                            <p className="text-sm">{t('termsOfService.s4.h1p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s4.h2')}</h3>
                                            <p className="text-sm">{t('termsOfService.s4.h2p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s4.h3')}</h3>
                                            <p className="text-sm">{t('termsOfService.s4.h3p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s4.h4')}</h3>
                                            <p className="text-sm mb-3">{t('termsOfService.s4.h4p1')}</p>
                                            <p className="text-sm">{t('termsOfService.s4.h4p2')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 5. CTC Copy Service */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <Copy size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-6">{t('termsOfService.s5.title')}</h2>
                                    <div className="space-y-5">
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s5.h1')}</h3>
                                            <p className="text-sm mb-3">{t('termsOfService.s5.h1p1')}</p>
                                            <p className="text-sm">{t('termsOfService.s5.h1p2')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s5.h2')}</h3>
                                            <p className="text-sm">{t('termsOfService.s5.h2p')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 6. AI & Automation */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <Cpu size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-6">{t('termsOfService.s6.title')}</h2>
                                    <div className="space-y-5">
                                        <p className="text-sm">{t('termsOfService.s6.p')}</p>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s6.h1')}</h3>
                                            <p className="text-sm">{t('termsOfService.s6.h1p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s6.h2')}</h3>
                                            <p className="text-sm">{t('termsOfService.s6.h2p')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 7. Real Estate */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-orange-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <Building size={24} className="text-orange-500" />
                                </div>
                                <div className="w-full">
                                    <h2 className="text-xl font-bold text-white mb-6">{t('termsOfService.s7.title')}</h2>
                                    <div className="space-y-5">
                                        <p className="text-sm">{t('termsOfService.s7.p')}</p>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s7.h1')}</h3>
                                            <p className="text-sm">{t('termsOfService.s7.h1p')}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">{t('termsOfService.s7.h2')}</h3>
                                            <p className="text-sm">{t('termsOfService.s7.h2p')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                    </div>
                </div>
            </div>
        </div>
    );
};
